import classNames from "classnames";
import { createElement, memo } from "react";

import { NavigationHandler } from "../../../../hooks";
import { RouteType } from "../../../../router/enums";
import { Routes, Route } from "../../../../router/types";
import { RoutesContainer } from "../RoutesContainer";

import styles from "./SingleRoute.module.scss";

type SingleRouteProps = {
  readonly children: number;
  readonly currentRoute: string;
  readonly isActive: boolean;
  readonly isValidated: boolean;
  readonly label: string;
  readonly onClickHandler: NavigationHandler;
  readonly type: RouteType;
  readonly subRoutes: Routes | undefined;
};

const getClasses = (
  type: RouteType,
  isActiveItem: boolean,
  isValidated: boolean
) => {
  const baseClass =
    type === RouteType.MAIN_ROUTE ? styles.item : styles.subitem;
  const routeClass = classNames(baseClass, {
    [styles["item-active"]]: isActiveItem,
    [styles["item-done"]]: isValidated,
  });
  const numberClass =
    type === RouteType.MAIN_ROUTE ? styles.item_number : styles.subitem_number;
  const descClass =
    type === RouteType.MAIN_ROUTE ? styles.item_desc : styles.subitem_desc;

  return { routeClass, numberClass, descClass };
};

const SingleRoute = memo<SingleRouteProps>(
  ({
    children,
    currentRoute,
    isActive,
    isValidated,
    label,
    onClickHandler,
    type,
    subRoutes,
  }) => {
    const hasSubRoutes =
      (type === RouteType.MAIN_ROUTE && subRoutes && true) || false;
    const { routeClass, numberClass, descClass } = getClasses(
      type,
      isActive,
      isValidated
    );

    if (type !== RouteType.MAIN_ROUTE) {
      return (
        <li className={routeClass}>
          <span className={numberClass}>{children}</span>
          <span className={descClass}>{label}</span>
        </li>
      );
    }

    return (
      <li className={routeClass}>
        <span className={styles.redirectItem} onClick={onClickHandler}>
          <span className={numberClass}>{children}</span>
          <span className={descClass}>{label}</span>
        </span>
        {isActive && hasSubRoutes && subRoutes && (
          <RoutesContainer currentRoute={currentRoute} routes={subRoutes} />
        )}
      </li>
    );
  }
);

export function createSingleRoute(
  route: Route &
    Pick<
      SingleRouteProps,
      | "isActive"
      | "isValidated"
      | "children"
      | "currentRoute"
      | "onClickHandler"
    >
) {
  const {
    children,
    currentRoute,
    isActive,
    isValidated,
    name,
    onClickHandler,
    routes,
    type,
  } = route;

  return createElement(SingleRoute, {
    children,
    currentRoute,
    key: name,
    isActive,
    isValidated,
    label: name,
    onClickHandler,
    subRoutes: routes,
    type,
  });
}
